import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

const LINKS = [
  { href: "#why", label: "Why Appraise" },
  { href: "#services", label: "Authentication" },
  { href: "#protection", label: "Brand Protection" },
  { href: "#categories", label: "Categories" },
  { href: "#about", label: "About" },
  { href: "#brands", label: "Brands" },
  { href: "#contact", label: "Contact" },
];

export const Wordmark = ({ className = "" }) => (
  <a href="#top" className={`flex flex-col leading-none shrink-0 ${className}`} data-testid="site-wordmark">
    <span className="font-serif-display text-lg tracking-wide text-[color:var(--laa-ivory)] whitespace-nowrap">
      Luxury Appraisals
    </span>
    <span className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--laa-champagne)] mt-0.5">
      & Authentication
    </span>
  </a>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 ${
        scrolled
          ? "bg-[rgba(14,15,16,0.88)] backdrop-blur-md border-b border-[color:var(--laa-hairline)]"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{ transition: "background-color 240ms cubic-bezier(0.2,0.8,0.2,1), border-color 240ms cubic-bezier(0.2,0.8,0.2,1)" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Wordmark />
        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="laa-link text-[13px] whitespace-nowrap text-[rgba(244,239,230,0.75)] hover:text-[color:var(--laa-ivory)]"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/design-options"
            data-testid="nav-link-design-options"
            className="laa-link text-[13px] whitespace-nowrap text-[color:var(--laa-champagne)]"
          >
            Design Options
          </Link>
        </nav>
        <div className="hidden lg:block shrink-0">
          <a href="https://www.thefashiondetective.com/" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="site-nav-request-appraisal-button"
              className="rounded-xl px-4 xl:px-5 text-sm font-medium bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--laa-champagne)] whitespace-nowrap"
            >
              Request an Appraisal
            </Button>
          </a>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <button
              data-testid="site-nav-mobile-menu-button"
              aria-label="Open menu"
              className="p-2 rounded-lg text-[color:var(--laa-ivory)] hover:bg-[rgba(244,239,230,0.06)]"
            >
              <Menu size={22} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-[color:var(--laa-ink-2)] border-l border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] flex flex-col"
          >
            <div className="mt-8 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg font-serif-display"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/design-options" onClick={() => setOpen(false)} className="text-lg font-serif-display text-[color:var(--laa-champagne)]">
                Design Options
              </Link>
            </div>
            <div className="mt-auto pb-6">
              <a href="https://www.thefashiondetective.com/" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                <Button className="w-full rounded-xl bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white">
                  Request an Appraisal
                </Button>
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
